import type { ReactNode } from 'react';
import { Button } from '@newcore/ui/buttons';
import { FontWeights } from '@newcore/ui/enums/font-weights';
import { Sizes } from '@newcore/ui/enums/sizes';
import { DisplaySizes } from '@newcore/ui/enums/typography-display-sizes';
import { TextSizes } from '@newcore/ui/enums/typography-text-sizes';
import { Display, Text } from '@newcore/ui/typography';
import { Onboarding } from '@/features/onboarding/components/primitives';
import { useMobileInstallApp } from '@/features/onboarding/hooks/use-mobile-install-app';
import {
  getAppStoreBadgeUrl,
  getNewcoreAppIconUrl,
  getPlayStoreBadgeUrl,
} from '@/features/onboarding/utils/install-page-assets';
import { InstallStep } from './install-step';
import { StoreBadge } from './store-badge';

const PLAY_STORE_BADGE_ALT = 'Get it on Google Play';
const APP_STORE_BADGE_ALT = 'Download on the App Store';
const APP_NOT_INSTALLED_NOTICE =
  'The NewCore app isn’t installed on this device. Install it, then tap Open NewCore again.';

interface MobileInstallAppProps {
  deepLinkUrl: string;
  title: string;
  children: ReactNode;
  notice?: string;
  playStoreReferrer?: string;
}

function storeName(showAppStore: boolean, showPlayStore: boolean) {
  if (showAppStore && showPlayStore) {
    return 'the App Store or Google Play';
  }

  return showAppStore ? 'the App Store' : 'Google Play';
}

export function MobileInstallApp({
  deepLinkUrl,
  title,
  notice,
  playStoreReferrer,
  children,
}: MobileInstallAppProps) {
  const {
    playStoreUrl,
    appStoreUrl,
    showPlayStore,
    showAppStore,
    isAppMissing,
    handleOpenApp,
  } = useMobileInstallApp({ deepLinkUrl, playStoreReferrer });

  return (
    <div className='h-dvh overflow-y-auto bg-primary'>
      <div className='mx-auto flex min-h-full w-full max-w-md flex-col px-6 pb-6 pt-10 text-center'>
        <div className='flex flex-col items-center gap-5'>
          <img
            src={getNewcoreAppIconUrl()}
            alt='NewCore'
            className='h-20 w-20 rounded-[18px]'
          />
          <Display
            as='h1'
            size={DisplaySizes.SM}
            weight={FontWeights.REGULAR}
            className='text-primary'
          >
            {title}
          </Display>
        </div>

        {notice && (
          <Text
            as='p'
            size={TextSizes.MD}
            className='mt-4 text-tertiary'
            data-testid='mobile-install-app-notice'
          >
            {notice}
          </Text>
        )}

        {isAppMissing && (
          <Onboarding.Error
            error={APP_NOT_INSTALLED_NOTICE}
            className='mt-6 text-left'
          />
        )}

        <ol
          className='mt-8 flex flex-col gap-[18px] rounded-[20px] bg-secondary p-5 text-left'
          data-testid='install-steps'
        >
          <InstallStep number={1}>
            Download NewCore from {storeName(showAppStore, showPlayStore)}
          </InstallStep>
          {children}
        </ol>

        <div className='mt-auto flex flex-col items-center pt-4 pb-3.5'>
          <div
            className='flex flex-col items-center'
            data-testid='store-badges'
          >
            {showAppStore && (
              <StoreBadge
                href={appStoreUrl}
                src={getAppStoreBadgeUrl()}
                alt={APP_STORE_BADGE_ALT}
                testId='app-store-badge'
              />
            )}
            {showPlayStore && (
              <StoreBadge
                href={playStoreUrl}
                src={getPlayStoreBadgeUrl()}
                alt={PLAY_STORE_BADGE_ALT}
                testId='play-store-badge'
              />
            )}
          </div>
          <Button
            size={Sizes.LG}
            onClick={handleOpenApp}
            className='w-full'
            data-testid='open-app-button'
          >
            Open NewCore
          </Button>
        </div>
      </div>
    </div>
  );
}
